import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Lab, 
  useListLabDetailsQuery,
  LabDetail as LabDetailType
} from '../lib/api';
import { API } from '../lib/fetcher';
import { onCreateLabDetail, onUpdateLabDetail, onDeleteLabDetail } from '../graphql/subscriptions';
import Observable from 'zen-observable';
import React from 'react';

const labOptions = Object.values(Lab);

// Define sort fields for type safety
type SortField = 'lab' | 'lastCreationTimeInSeconds' | 'lastDurationInSeconds' | 'lastTimeStarted' | 'lastTimeEnded' | 'historyCount';
type SortDirection = 'asc' | 'desc';

// Define history sort fields
type HistorySortField = 'userId' | 'creationTimeInSeconds' | 'durationInSeconds' | 'timeStarted' | 'timeEnded';

export function LabDetail() {
  const [selectedLab, setSelectedLab] = useState<Lab | ''>('');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [historySortField, setHistorySortField] = useState<HistorySortField | null>(null);
  const [historySortDirection, setHistorySortDirection] = useState<SortDirection>('asc');
  const location = useLocation();

  // Parse query params if any
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lab = params.get('lab');
    if (lab && Object.values(Lab).includes(lab as Lab)) {
      setSelectedLab(lab as Lab);
    }
  }, [location]);

  // Set up the filter based on selectedLab
  const filter = selectedLab ? { lab: { eq: selectedLab } } : undefined;

  const { data, isLoading, refetch } = useListLabDetailsQuery({
    filter: filter,
    limit: 100,
  }, {
    refetchOnWindowFocus: false,
    queryKey: ['listLabDetails'],
  });

  // Sort lab details
  const sortedLabDetails = React.useMemo(() => {
    if (!data?.listLabDetails.items) return [];
    
    // First sort alphabetically by lab name (default sort)
    let sorted = [...data.listLabDetails.items].sort((a, b) => 
      a.lab.localeCompare(b.lab)
    );
    
    // Then apply custom sorting if a sort field is selected
    if (sortField) {
      sorted = sorted.sort((a, b) => {
        // Handle different field types
        let valueA: any;
        let valueB: any;
        
        switch (sortField) {
          case 'lab':
            valueA = a.lab;
            valueB = b.lab;
            break;
          case 'lastCreationTimeInSeconds':
            valueA = a.lastCreationTimeInSeconds || 0;
            valueB = b.lastCreationTimeInSeconds || 0;
            break;
          case 'lastDurationInSeconds':
            valueA = a.lastDurationInSeconds || 0;
            valueB = b.lastDurationInSeconds || 0;
            break;
          case 'lastTimeStarted':
            valueA = a.lastTimeStarted || 0;
            valueB = b.lastTimeStarted || 0;
            break;
          case 'lastTimeEnded':
            valueA = a.lastTimeEnded || 0;
            valueB = b.lastTimeEnded || 0;
            break;
          case 'historyCount':
            valueA = a.history?.items.length || 0;
            valueB = b.history?.items.length || 0;
            break;
          default:
            return 0;
        }
        
        // Sort based on direction
        if (sortDirection === 'asc') {
          return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
        } else {
          return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
        }
      });
    }
    
    return sorted;
  }, [data?.listLabDetails.items, sortField, sortDirection]);

  // Handle sorting when a header is clicked
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field is clicked
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) {
      return (
        <span className="ml-1 text-gray-400">
          <svg className="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    
    return sortDirection === 'asc' ? (
      <span className="ml-1 text-blue-600">
        <svg className="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      </span>
    ) : (
      <span className="ml-1 text-blue-600">
        <svg className="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </span>
    );
  };

  // Handle sorting for history items
  const handleHistorySort = (field: HistorySortField) => {
    if (historySortField === field) {
      // Toggle direction if same field is clicked
      setHistorySortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset to ascending
      setHistorySortField(field);
      setHistorySortDirection('asc');
    }
  };

  // Render sort indicator for history headers
  const renderHistorySortIndicator = (field: HistorySortField) => {
    if (historySortField !== field) {
      return (
        <span className="ml-1 text-gray-400">
          <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    
    return historySortDirection === 'asc' ? (
      <span className="ml-1 text-blue-600">
        <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      </span>
    ) : (
      <span className="ml-1 text-blue-600">
        <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </span>
    );
  };

  // Sort history items
  const sortHistoryItems = (historyItems: any[]) => {
    if (!historyItems || historyItems.length === 0 || !historySortField) {
      return historyItems;
    }
    
    return [...historyItems].sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (historySortField) {
        case 'userId':
          valueA = a.userId || '';
          valueB = b.userId || '';
          break;
        case 'creationTimeInSeconds':
          valueA = a.creationTimeInSeconds || 0;
          valueB = b.creationTimeInSeconds || 0;
          break;
        case 'durationInSeconds':
          valueA = a.durationInSeconds || 0;
          valueB = b.durationInSeconds || 0;
          break;
        case 'timeStarted':
          valueA = a.timeStarted || 0;
          valueB = b.timeStarted || 0;
          break;
        case 'timeEnded':
          valueA = a.timeEnded || 0;
          valueB = b.timeEnded || 0;
          break;
        default:
          return 0;
      }
      
      // For string values like userId
      if (typeof valueA === 'string') {
        return historySortDirection === 'asc'
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }
      
      // For numeric values
      return historySortDirection === 'asc'
        ? valueA - valueB
        : valueB - valueA;
    });
  };

  useEffect(() => {
    subscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async () => {
    // Subscribe to LabDetail changes
    const createSub = (await API.getInstance().query(
      onCreateLabDetail
    )) as Observable<any>;
    createSub.subscribe({
      next: async () => {
        await refetch();
      },
      error: (error) => console.warn(error),
    });

    const updateSub = (await API.getInstance().query(
      onUpdateLabDetail
    )) as Observable<any>;
    updateSub.subscribe({
      next: async () => {
        await refetch();
      },
      error: (error) => console.warn(error),
    });

    const deleteSub = (await API.getInstance().query(
      onDeleteLabDetail
    )) as Observable<any>;
    deleteSub.subscribe({
      next: async () => {
        await refetch();
      },
      error: (error) => console.warn(error),
    });
  };

  const handleLabChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLab(event.target.value as Lab);
  };

  // Format timestamps to readable dates
  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Format duration to show minutes and seconds if > 60s
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Calculate average duration
  const calculateAverageDuration = (labDetails: LabDetailType[]) => {
    if (!labDetails || labDetails.length === 0) return '-';
    
    let totalDuration = 0;
    let count = 0;
    
    labDetails.forEach(item => {
      if (item.lastDurationInSeconds) {
        totalDuration += item.lastDurationInSeconds;
        count++;
      }
    });
    
    if (count === 0) return '-';
    
    const avgSeconds = totalDuration / count;
    const minutes = Math.floor(avgSeconds / 60);
    const seconds = Math.floor(avgSeconds % 60);
    
    return `${minutes}m ${seconds}s`;
  };

  // Toggle row expansion
  const toggleRowExpand = (id: string) => {
    setExpandedRows(prev => {
      console.log('Toggling row:', id, 'Current state:', prev[id]);
      return {
        ...prev,
        [id]: !prev[id]
      };
    });
  };

  // Log expanded rows when it changes
  useEffect(() => {
    console.log('Expanded rows state:', expandedRows);
  }, [expandedRows]);

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-blue-600 text-white px-6 py-4">
          <h4 className="text-xl font-semibold mb-1">Lab Details</h4>
          <p className="text-sm">View statistics for laboratory environments</p>
        </div>
        <div className="p-6">
          <div className="mb-5">
            <label htmlFor="lab-select" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Lab
            </label>
            <select
              data-testid="lab-select"
              id="lab-select"
              value={selectedLab}
              onChange={handleLabChange}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
            >
              <option value="">All Labs</option>
              {labOptions.map((lab) => (
                <option key={lab} value={lab}>
                  {lab}
                </option>
              ))}
            </select>
          </div>

          {isLoading ? (
            <div className="py-4 text-center text-gray-600">Loading lab details...</div>
          ) : (
            <>
              <div className="mb-5 text-sm text-gray-700">
                <span className="font-bold">Total Records:</span> {sortedLabDetails.length || 0}
                {sortedLabDetails.length ? (
                  <span className="ml-2">
                    <span className="font-bold">Average Duration:</span> {calculateAverageDuration(sortedLabDetails)}
                  </span>
                ) : null}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="w-10"></th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lab')}
                      >
                        Lab {renderSortIndicator('lab')}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastCreationTimeInSeconds')}
                      >
                        Last Creation Duration Time {renderSortIndicator('lastCreationTimeInSeconds')}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastDurationInSeconds')}
                      >
                        Last Duration {renderSortIndicator('lastDurationInSeconds')}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastTimeStarted')}
                      >
                        Last Time Started {renderSortIndicator('lastTimeStarted')}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('lastTimeEnded')}
                      >
                        Last Time Ended {renderSortIndicator('lastTimeEnded')}
                      </th>
                      <th 
                        scope="col" 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('historyCount')}
                      >
                        History Count {renderSortIndicator('historyCount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedLabDetails.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-2 py-4 whitespace-nowrap">
                            <button
                              aria-label={expandedRows[item.id] ? "collapse" : "expand"} 
                              className="p-1 rounded-full hover:bg-gray-200 focus:outline-none"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRowExpand(item.id);
                              }}
                              data-testid={`expand-row-${item.id}`}
                            >
                              <svg className={`h-5 w-5 transition-transform duration-200 ${expandedRows[item.id] ? 'transform rotate-180' : ''}`} 
                                fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" 
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                                  clipRule="evenodd" />
                              </svg>
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{item.lab}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{formatDuration(item.lastCreationTimeInSeconds)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{formatDuration(item.lastDurationInSeconds)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{formatTimestamp(item.lastTimeStarted)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{formatTimestamp(item.lastTimeEnded)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{item.history?.items.length || 0}</td>
                        </tr>
                        <tr className={!expandedRows[item.id] ? 'hidden' : ''}>
                          <td colSpan={7} className="px-0 py-0">
                            <div className={`transition-all duration-300 ${expandedRows[item.id] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                              <div className="px-8 py-4 bg-gray-50">
                                <h6 className="text-base font-medium text-gray-900 mb-3">
                                  History
                                </h6>
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th 
                                        scope="col" 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                                        onClick={() => handleHistorySort('userId')}
                                      >
                                        User {renderHistorySortIndicator('userId')}
                                      </th>
                                      <th 
                                        scope="col" 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                                        onClick={() => handleHistorySort('creationTimeInSeconds')}
                                      >
                                        Creation Time Duration {renderHistorySortIndicator('creationTimeInSeconds')}
                                      </th>
                                      <th 
                                        scope="col" 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                                        onClick={() => handleHistorySort('durationInSeconds')}
                                      >
                                        Duration {renderHistorySortIndicator('durationInSeconds')}
                                      </th>
                                      <th 
                                        scope="col" 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                                        onClick={() => handleHistorySort('timeStarted')}
                                      >
                                        Time Started {renderHistorySortIndicator('timeStarted')}
                                      </th>
                                      <th 
                                        scope="col" 
                                        className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                                        onClick={() => handleHistorySort('timeEnded')}
                                      >
                                        Time Ended {renderHistorySortIndicator('timeEnded')}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {item.history?.items.length ? (
                                      sortHistoryItems(item.history.items).map((historyItem) => (
                                        <tr key={historyItem.id} className="hover:bg-gray-50">
                                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{historyItem.userId}</td>
                                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{formatDuration(historyItem.creationTimeInSeconds)}</td>
                                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{formatDuration(historyItem.durationInSeconds)}</td>
                                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{formatTimestamp(historyItem.timeStarted)}</td>
                                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{formatTimestamp(historyItem.timeEnded)}</td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan={5} className="px-6 py-4 text-sm text-center text-gray-500">
                                          No history available
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                    {!sortedLabDetails.length && (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                          No lab details found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => refetch()}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
} 